import { Box, Paper, Skeleton, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import InfoTooltip from './InfoTooltip'

interface KpiBigNumberProps {
  label: string
  // null signals "data not loaded yet" — renders a Skeleton in place of the number.
  value: ReactNode | null
  sublabel?: ReactNode
  variant?: 'compact' | 'wall'
  accentColor?: string
  info?: string
  onClick?: () => void
  // When this KpiBigNumber sits inside another card (e.g. bucket inside OpenByTypeCard),
  // reduce padding and bump the cqi so the number reads at a comparable visual weight to
  // a free-standing KPI card.
  dense?: boolean
  // Optional trend data — if provided and has at least 2 points, render a mini sparkline
  // under the number. Color defaults to accentColor.
  sparkline?: readonly number[]
}

function KpiBigNumber({ label, value, sublabel, variant = 'compact', accentColor, info, onClick, dense = false, sparkline }: KpiBigNumberProps) {
  const isWall = variant === 'wall'
  const showSparkline = sparkline !== undefined && sparkline.length >= 2 && !isWall
  return (
    <Paper
      elevation={2}
      onClick={onClick}
      sx={{
        p: isWall ? (dense ? 1.5 : { xs: 2, md: 3, xl: 4 }) : { xs: 1.5, sm: 2 },
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 120ms ease',
        '&:hover': onClick ? { transform: 'translateY(-2px)' } : undefined,
        minWidth: 0,
        textAlign: 'center',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderTop: accentColor ? `4px solid ${accentColor}` : undefined,
        position: 'relative',
        // Establish this card as a query container so the big number can be sized relative to
        // the card's own width (cqi) instead of the viewport (vw). That way a narrow bucket
        // inside OpenByTypeCard gets a smaller font than the wide grand-total card next to it,
        // and two-digit numbers don't overflow on a regular PC monitor.
        containerType: 'inline-size',
      }}
    >
      {info && !isWall && (
        <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
          <InfoTooltip text={info} />
        </Box>
      )}
      <Typography
        sx={{
          fontSize: isWall ? 'clamp(0.875rem, 1.2vw, 1.375rem)' : undefined,
          display: 'block',
        }}
        variant={isWall ? undefined : 'body2'}
        color="text.secondary"
        gutterBottom
      >
        {label}
      </Typography>
      <Box
        sx={{
          // cqi scales the number with the card's own width. dense = nested-in-another-card,
          // we lean larger because the reduced padding leaves more room.
          fontSize: isWall ? (dense ? 'clamp(2.5rem, 60cqi, 9rem)' : 'clamp(2.5rem, 45cqi, 9rem)') : 'clamp(1.5rem, 3vw, 2.25rem)',
          fontWeight: 700,
          lineHeight: 1,
          color: accentColor,
        }}
      >
        {value === null ? <Skeleton variant="text" width="50%" sx={{ mx: 'auto', fontSize: 'inherit' }} /> : value}
      </Box>
      {sublabel && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: isWall ? 'clamp(0.75rem, 1vw, 1rem)' : undefined }}>
          {sublabel}
        </Typography>
      )}
      {showSparkline && (
        <Box sx={{ mt: 0.5, height: 28, opacity: 0.85 }}>
          <SparkLineChart
            data={[...sparkline]}
            height={28}
            curve="monotoneX"
            color={accentColor}
            showHighlight
            showTooltip
            valueFormatter={(v) => (v === null ? '' : String(v))}
            margin={{ top: 2, bottom: 2, left: 0, right: 0 }}
            slotProps={{ tooltip: { position: 'top' } }}
          />
        </Box>
      )}
    </Paper>
  )
}

export default KpiBigNumber
